<script setup lang="ts">
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue';
import {useRouter} from 'vue-router';
import AppMenuItemLinha from './AppMenuItemLinha.vue';
import {getMenuModulos} from './menu.config';
import {filtrarFolhasPorTermo, obterTodasAsFolhas} from './menu.util';
import {useEventBus} from '@/app/events/useEventBus';
import type {FolhaComContexto, MenuItemFolha, MenuModulo} from './menu.type';

const {on} = useEventBus();
const router = useRouter();

const aberto = ref(false);
const termo = ref('');
const inputRef = ref<HTMLInputElement | null>(null);
const containerResultadosRef = ref<HTMLElement | null>(null);

const modulos = computed<MenuModulo[]>(() => getMenuModulos());
const todasAsFolhas = computed<FolhaComContexto[]>(() => obterTodasAsFolhas(modulos.value));
const termoBusca = computed(() => termo.value.trim());
const resultados = computed<FolhaComContexto[]>(() => filtrarFolhasPorTermo(todasAsFolhas.value, termo.value));

const indiceAtivo = ref(0);

watch(resultados, () => {
    indiceAtivo.value = 0;
});

const focarInput = () => {
    inputRef.value?.focus();
};

const aoFecharDialog = () => {
    termo.value = '';
    indiceAtivo.value = 0;
};

const fechar = () => {
    aberto.value = false;
};

const aoMoverSelecao = (delta: number) => {
    const total = resultados.value.length;

    if (!total) {
        return;
    }

    indiceAtivo.value = (indiceAtivo.value + delta + total) % total;

    nextTick(() => {
        const links = containerResultadosRef.value?.querySelectorAll<HTMLElement>('.menu-item__link');
        links?.[indiceAtivo.value]?.scrollIntoView({block: 'nearest'});
    });
};

// Abre a tela sem sair da busca: mesmo destino do clique normal, só que numa aba nova — não usa
// `.click()` no <a>/<router-link> (que navegaria na aba atual) nem `target="_blank"` (afetaria o
// clique com mouse também).
const abrirEmNovaGuia = (item: MenuItemFolha) => {
    if (item.to) {
        window.open(router.resolve(item.to).href, '_blank', 'noopener,noreferrer');
        return;
    }

    if (item.url) {
        window.open(item.url, '_blank', 'noopener,noreferrer');
        return;
    }

    // Item de comando (ex.: "Atividades") não tem rota própria — o próprio comando decide como abrir.
    item.command?.({originalEvent: new MouseEvent('click'), item});
};

const aoPressionarEnter = (evento: KeyboardEvent) => {
    const folha = resultados.value[indiceAtivo.value];

    if (!folha) {
        return;
    }

    if (evento.ctrlKey || evento.metaKey) {
        abrirEmNovaGuia(folha.item);
        fechar();
        return;
    }

    const links = containerResultadosRef.value?.querySelectorAll<HTMLElement>('.menu-item__link');
    links?.[indiceAtivo.value]?.click();
};

const aoPressionarTeclaGlobal = (evento: KeyboardEvent) => {
    const teclaK = evento.key.toLowerCase() === 'k';

    if (teclaK && (evento.ctrlKey || evento.metaKey)) {
        evento.preventDefault();
        aberto.value = !aberto.value;
    }
};

on('system:buscaGlobalAberta', () => {
    aberto.value = true;
});

onMounted(() => {
    document.addEventListener('keydown', aoPressionarTeclaGlobal);
});

onUnmounted(() => {
    document.removeEventListener('keydown', aoPressionarTeclaGlobal);
});
</script>

<template>
    <Dialog
        v-model:visible="aberto"
        modal
        dismissable-mask
        :draggable="false"
        :show-header="false"
        position="top"
        :style="{width: '38rem', marginTop: '10vh'}"
        content-class="paleta-comandos__conteudo"
        class="paleta-comandos"
        @show="focarInput"
        @hide="aoFecharDialog"
    >
        <div class="paleta-comandos__busca">
            <i class="pi pi-search paleta-comandos__icone" aria-hidden="true"></i>
            <input
                ref="inputRef"
                v-model="termo"
                type="search"
                class="paleta-comandos__input"
                placeholder="Buscar tela…"
                aria-label="Buscar tela"
                autocomplete="off"
                @keydown.enter="aoPressionarEnter"
                @keydown.down.prevent="aoMoverSelecao(1)"
                @keydown.up.prevent="aoMoverSelecao(-1)"
            />
            <span class="paleta-comandos__esc" @click="fechar">Esc</span>
        </div>

        <div ref="containerResultadosRef" class="menu-lista paleta-comandos__lista" @click="fechar">
            <template v-if="termoBusca">
                <div class="menu-secao-label">
                    {{ resultados.length }} {{ resultados.length === 1 ? 'tela encontrada' : 'telas encontradas' }}
                </div>
                <app-menu-item-linha
                    v-for="(folha, index) in resultados"
                    :key="folha.item.id"
                    :item="folha.item"
                    :contexto="folha.modulo.label"
                    :destacado="index === indiceAtivo"
                    mostrar-descricao
                ></app-menu-item-linha>
                <div v-if="!resultados.length" class="paleta-comandos__vazio">Nenhuma tela encontrada.</div>
            </template>
            <div v-else class="paleta-comandos__vazio">Digite para buscar…</div>
        </div>

        <div class="paleta-comandos__rodape">
            <span class="paleta-comandos__dica"><kbd>↵</kbd> selecionar</span>
            <span class="paleta-comandos__dica"><kbd>ctrl</kbd><kbd>↵</kbd> nova guia</span>
            <span class="paleta-comandos__dica"><kbd>↓</kbd><kbd>↑</kbd> navegar</span>
            <span class="paleta-comandos__dica" @click="fechar"><kbd>esc</kbd> fechar</span>
        </div>
    </Dialog>
</template>

<style lang="scss" scoped>
.paleta-comandos__conteudo {
    padding: 0;
}

.paleta-comandos__busca {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--surface-border);
}

.paleta-comandos__icone {
    color: var(--text-color-secondary);
    font-size: 1rem;
}

.paleta-comandos__input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    color: var(--text-color);
    font-size: 1.05rem;

    &::placeholder {
        color: var(--text-color-secondary);
    }

    &:focus {
        outline: none;
    }
}

.paleta-comandos__esc {
    flex-shrink: 0;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--text-color-secondary);
    border: 1px solid var(--surface-border);
    border-radius: 6px;
    padding: 0.15rem 0.5rem;
    cursor: pointer;
}

.paleta-comandos__lista {
    max-height: 60vh;
    overflow-y: auto;
    padding: 0.5rem;
}

.paleta-comandos__vazio {
    padding: 2.5rem 0;
    text-align: center;
    color: var(--text-color-secondary);
}

.paleta-comandos__rodape {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    padding: 0.6rem 1.25rem;
    border-top: 1px solid var(--surface-border);
    color: var(--text-color-secondary);
    font-size: 0.78rem;
}

.paleta-comandos__dica {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;

    kbd {
        font-family: inherit;
        font-size: 0.7rem;
        font-weight: 600;
        line-height: 1;
        padding: 0.2rem 0.4rem;
        border-radius: 5px;
        border: 1px solid var(--surface-border);
        background-color: var(--surface-card);
        color: var(--text-color-secondary);
    }
}
</style>
